/** The photo report's positioning section: where the camera positions come from, overall and per flight. */
import { Group, ScrollArea, SimpleGrid, Stack, Table, Text, Tooltip } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { StatCard } from '@/components/StatCard';
import { useFormat } from '@/components/useFormat';
import { FindingsList } from '@/features/runs/FindingsList';
import { POSITION_SOURCE } from '@/theme/colors';
import { brand } from '@/theme/theme';
import { Section } from '../trees/Section';
import type { Anchoring, PositioningStats, PpkLog, SourceShare } from './positioning';

/** The share of each source as one bar, the sources' colours as on the map. */
function SourceBar({ sources, testId }: { sources: SourceShare[]; testId?: string }) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  if (!sources.length) return null;
  return (
    <div data-testid={testId} style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', border: `1px solid ${brand.border}`, minWidth: 80 }}>
      {sources.map((s) => (
        <Tooltip key={s.source} label={`${t(`photos.positioning.source.${s.source}`)} ${number(s.pct, 0)} %`}>
          <div style={{ width: `${s.pct}%`, background: POSITION_SOURCE[s.source] }} />
        </Tooltip>
      ))}
    </div>
  );
}

function Swatch({ color }: { color: string }) {
  return <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 5, background: color, flexShrink: 0 }} />;
}

function SourceList({ sources }: { sources: SourceShare[] }) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  return (
    <Stack gap={2}>
      {sources.map((s) => (
        <Group key={s.source} gap={6} wrap="nowrap">
          <Swatch color={POSITION_SOURCE[s.source]} />
          <Text fz={13}>
            {t(`photos.positioning.source.${s.source}`)} {number(s.pct, 0)} %
          </Text>
        </Group>
      ))}
    </Stack>
  );
}

function PpkCell({ logs }: { logs: PpkLog[] }) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  if (!logs.length) return <>—</>;
  return (
    <Stack gap={2}>
      {logs.map((log) => {
        const fixed = log.fixed_fraction === null ? null : number(100 * log.fixed_fraction, 0);
        const outcome = fixed === null ? t('photos.positioning.ppk.notSolved') : t(log.applied ? 'photos.positioning.ppk.applied' : 'photos.positioning.ppk.notApplied', { fixed });
        const base =
          log.base_source === 'czepos'
            ? log.base_offset_km
              ? t('photos.positioning.ppk.czeposOffset', { km: number(log.base_offset_km, 1) })
              : t('photos.positioning.ppk.czepos')
            : log.base_source === 'delivery'
              ? t('photos.positioning.ppk.delivery')
              : null;
        const parts = [outcome, base, log.order_id ? t('photos.positioning.ppk.order', { id: log.order_id }) : null].filter(Boolean);
        return (
          <Text key={log.log} fz={13} title={log.log}>
            {parts.join(' · ')}
          </Text>
        );
      })}
    </Stack>
  );
}

const OVERLAP_SKIPPED = ['low_coverage', 'too_few_pairs', 'offset_scattered', 'offset_disagrees_with_takeoff'];

function AnchoringCell({ anchoring }: { anchoring: Anchoring | null }) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  if (!anchoring) return <>—</>;
  const m = (v: number | null) => (v === null ? '—' : number(v, 2));
  let text: string;
  if (!anchoring.anchored) text = t('photos.positioning.anchoring.notAnchored');
  else if (anchoring.anchored_by === 'takeoff')
    text =
      anchoring.takeoff_difference_m === null
        ? t('photos.positioning.anchoring.takeoffNoDiff', { flight: anchoring.source_flight ?? '—' })
        : t('photos.positioning.anchoring.takeoff', { flight: anchoring.source_flight ?? '—', diff: m(anchoring.takeoff_difference_m) });
  else
    text = anchoring.offset_applied
      ? t('photos.positioning.anchoring.overlap', { offset: m(anchoring.offset_m), iqr: m(anchoring.spread_iqr_m) })
      : t('photos.positioning.anchoring.overlapNotApplied', { iqr: m(anchoring.spread_iqr_m) });
  // Why the overlap gave way to the take-off; a reason this page does not know stays unsaid.
  if (anchoring.anchored && anchoring.anchored_by === 'takeoff' && anchoring.overlap_skipped && OVERLAP_SKIPPED.includes(anchoring.overlap_skipped))
    text += `; ${t(`photos.positioning.anchoring.overlapSkipped.${anchoring.overlap_skipped}`)}`;
  return <Text fz={13}>{text}</Text>;
}

export function PositioningSection({ stats }: { stats: PositioningStats }) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  const measured = stats.rtkPct + stats.ppkPct;
  const acc = (h: number | null, v: number | null) => (h === null && v === null ? '—' : `${h === null ? '—' : number(h, 2)} / ${v === null ? '—' : number(v, 2)} m`);
  return (
    <Section title={t('photos.positioning.title')}>
      <Stack gap="md" data-testid="photo-positioning">
        <Text fz={13} c={brand.textMuted}>
          {t('photos.positioning.intro')}
        </Text>
        {stats.total > 0 && (
          <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing="md">
            <StatCard
              label={t('photos.positioning.kpi.measured')}
              value={number(measured, 0)}
              unit="%"
              sub={t('photos.positioning.kpi.measuredSub', { rtk: number(stats.rtkPct, 0), ppk: number(stats.ppkPct, 0) })}
              testId="positioning-measured"
            />
            {stats.accH && <StatCard label={t('photos.positioning.kpi.accH')} value={number(stats.accH.median, 2)} unit="m" sub={t('photos.positioning.kpi.accP95', { value: number(stats.accH.p95, 2) })} />}
            {stats.accV && <StatCard label={t('photos.positioning.kpi.accV')} value={number(stats.accV.median, 2)} unit="m" sub={t('photos.positioning.kpi.accP95', { value: number(stats.accV.p95, 2) })} />}
            {stats.anchoredCount > 0 && (
              <StatCard
                label={t('photos.positioning.kpi.anchored')}
                value={number(stats.anchoredCount)}
                sub={t('photos.positioning.kpi.anchoredSub', { pct: number((100 * stats.anchoredCount) / stats.total, 0) })}
              />
            )}
          </SimpleGrid>
        )}
        {stats.sources.length > 0 && (
          <Stack gap={6}>
            <Text fz={13} c={brand.textMuted}>
              {t('photos.positioning.mix')}
            </Text>
            <SourceBar sources={stats.sources} testId="positioning-mix" />
            <Group gap="md">
              {stats.sources.map((s) => (
                <Group key={s.source} gap={6} wrap="nowrap" data-testid={`positioning-source-${s.source}`}>
                  <Swatch color={POSITION_SOURCE[s.source]} />
                  <Text fz={13}>
                    {t(`photos.positioning.source.${s.source}`)} {number(s.count)} ({number(s.pct, s.pct < 1 ? 1 : 0)} %)
                  </Text>
                </Group>
              ))}
            </Group>
            {stats.takeoffAccuracy && (
              <Text fz={12} c={brand.textMuted} data-testid="positioning-takeoff-accuracy">
                {t('photos.positioning.takeoffAccuracy', { h: number(stats.takeoffAccuracy[0], 1), v: number(stats.takeoffAccuracy[1], 1) })}
              </Text>
            )}
          </Stack>
        )}
        <ScrollArea type="auto">
          <Table verticalSpacing="xs" fz="sm" data-testid="positioning-flights">
            <Table.Thead>
              <Table.Tr>
                {(['flight', 'photos', 'sources', 'accuracy', 'ppk', 'anchoring'] as const).map((k) => (
                  <Table.Th key={k}>{t(`photos.positioning.table.${k}`)}</Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {stats.flights.map((f) => (
                <Table.Tr key={f.key} data-testid={`positioning-flight-${f.flight}`}>
                  <Table.Td fw={600}>{f.flight}</Table.Td>
                  <Table.Td>{f.photos === null ? '—' : number(f.photos)}</Table.Td>
                  <Table.Td>{f.sources.length ? <SourceList sources={f.sources} /> : '—'}</Table.Td>
                  <Table.Td style={{ whiteSpace: 'nowrap' }}>{acc(f.accH, f.accV)}</Table.Td>
                  <Table.Td>
                    <PpkCell logs={f.ppk} />
                  </Table.Td>
                  <Table.Td>
                    <AnchoringCell anchoring={f.anchoring} />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
        {stats.missingPositionsFile && (
          <Text fz={12} c={brand.textMuted}>
            {t('photos.positioning.noPositionsFile')}
          </Text>
        )}
        {stats.findings.length > 0 && (
          <Stack gap={6}>
            <Text fz={13} c={brand.textMuted}>
              {t('photos.positioning.findings')}
            </Text>
            <FindingsList findings={stats.findings} compact />
          </Stack>
        )}
      </Stack>
    </Section>
  );
}
